import { Component, input, output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Pricing } from '@hjc/masterdata-types';
import { PageSectionHeaderComponent } from '../page-section-header/page-section-header.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedTranslationModule } from '@shared/translation.module';
import { inject } from '@angular/core';

export type BulkPriceUpdateRequest = {
  itemIds: string[];
  changeFactor: number;
  changeType: 'percentage' | 'absolute';
  validFrom: Date | string;
};

type BulkOperationForm = {
  changeFactor: FormControl<number>;
  changeType: FormControl<'percentage' | 'absolute'>;
  validFrom: FormControl<Date | string | null>;
};

@Component({
  selector: 'md-pricing-bulk-operations-form',
  imports: [
    PageSectionHeaderComponent,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    TranslateModule,
    SharedTranslationModule
  ],
  templateUrl: './pricing-bulk-operations-form.component.html',
  styleUrl: './pricing-bulk-operations-form.component.scss',
  standalone: true
})
export class PricingBulkOperationsFormComponent {
  private translate = inject(TranslateService);

  selectedPrices = input<Pricing[]>([]);
  isLoading = input<boolean>(false);
  updateRequest = output<BulkPriceUpdateRequest>();

  bulkOperationForm: FormGroup<BulkOperationForm>;

  typeOptions = [
    {
      label: 'BULK_OPERATIONS.TYPE_OPTIONS.PERCENTAGE',
      value: 'percentage'
    },
    {
      label: 'BULK_OPERATIONS.TYPE_OPTIONS.ABSOLUTE',
      value: 'absolute'
    }
  ];

  constructor() {
    this.bulkOperationForm = new FormGroup({
      changeFactor: new FormControl(0, { nonNullable: true, validators: [factorValidator(), Validators.required] }),
      changeType: new FormControl<'percentage' | 'absolute'>('percentage', { nonNullable: true, validators: Validators.required }),
      validFrom: new FormControl<Date | string | null>(null, { validators: Validators.required })
    });
  }

  submitChanges() {
    if (this.bulkOperationForm.valid) {
      const { changeFactor, changeType, validFrom } = this.bulkOperationForm.getRawValue();
      const itemIds = this.selectedPrices().map(item => item.id);

      this.updateRequest.emit({
        itemIds,
        changeFactor,
        changeType,
        validFrom: new Date(validFrom!).toISOString()
      });

      this.bulkOperationForm.reset();
    }
  }
}

function factorValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === 0) {
      return { factor: 'Factor cannot be 0' };
    }
    return null;
  };
}
